// src/pages/AccountsListPage.tsx
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { baseClient } from '../api/baseClient';
import { accountsApi } from '../api';
import { 
  Table, TableBody, TableCell, TableHead, TableRow, Typography, 
  Paper, CircularProgress, Box, Button, TextField, Grid, 
  Dialog, DialogTitle, DialogContent, DialogActions, Link
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useState } from 'react';

// Define the type for an account to match our backend
interface Account {
  id: string;
  name: string;
  domain: string;
}

interface AccountFormData {
  name: string;
  domain: string;
}

// The fetching function
const fetchAccounts = async (): Promise<Account[]> => {
  try {
    console.log('Fetching accounts...');
    return await accountsApi.getAccounts();
  } catch (error) {
    console.error('Error fetching accounts:', error);
    throw error;
  }
};

export function AccountsListPage() {
  const [errorDetails, setErrorDetails] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState<AccountFormData>({
    name: '',
    domain: ''
  });
  const [formError, setFormError] = useState('');
  
  const queryClient = useQueryClient();
  
  const { data: accounts, isLoading, isError, error, refetch } = useQuery<Account[]>({ 
    queryKey: ['accounts'], 
    queryFn: fetchAccounts,
    retry: 1,
    onError: (err: any) => {
      console.error('Query error:', err);
      setErrorDetails(
        `Status: ${err?.response?.status || 'Unknown'}\n` +
        `Message: ${err?.message || 'Unknown error'}\n` +
        `Response: ${JSON.stringify(err?.response?.data || {}, null, 2)}`
      );
    }
  });

  const createAccountMutation = useMutation({
    mutationFn: (newAccount: AccountFormData) => {
      return accountsApi.createAccount(newAccount);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setDialogOpen(false);
      setFormData({ name: '', domain: '' });
      setFormError('');
    },
    onError: (err: any) => {
      console.error('Error creating account:', err);
      setFormError(err.response?.data?.detail || 'Failed to create account');
    }
  });

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      setFormError('Name is required');
      return;
    }
    if (!formData.domain.trim()) {
      setFormError('Domain is required');
      return;
    }
    
    createAccountMutation.mutate(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  if (isError) {
    return (
      <Box sx={{ margin: 2, padding: 2 }}>
        <Typography color="error" variant="h5" gutterBottom>Error fetching accounts</Typography>
        <Typography color="error" gutterBottom>
          {(error as Error)?.message || 'Unknown error'}
        </Typography>
        {errorDetails && (
          <Paper sx={{ p: 2, my: 2, bgcolor: '#f5f5f5', maxHeight: '200px', overflow: 'auto' }}>
            <pre>{errorDetails}</pre>
          </Paper>
        )}
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => refetch()}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Paper sx={{ margin: 2, padding: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Accounts</Typography>
        <Box>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => setDialogOpen(true)}
            sx={{ mr: 1 }}
          >
            Add Account
          </Button>
          <Button variant="outlined" onClick={() => refetch()}>Refresh</Button>
        </Box>
      </Box>
      
      {accounts && accounts.length > 0 ? (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Domain</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accounts.map((account) => (
              <TableRow key={account.id}>
                <TableCell>
                  <Link component={RouterLink} to={`/accounts/${account.id}`}>
                    {account.name}
                  </Link>
                </TableCell>
                <TableCell>{account.domain}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body1" sx={{ py: 4, textAlign: 'center' }}>
          No accounts found. Please add some accounts to get started.
        </Typography>
      )}

      {/* Add Account Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Add New Account</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={formError.includes('Name')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Domain"
                name="domain"
                value={formData.domain}
                onChange={handleChange}
                error={formError.includes('Domain')}
                helperText="e.g. example.com"
              />
            </Grid>
            {formError && (
              <Grid item xs={12}>
                <Typography color="error">{formError}</Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={createAccountMutation.isPending}
          >
            {createAccountMutation.isPending ? 'Adding...' : 'Add Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}